import React from 'react';
import styled from '@emotion/styled/macro';
import ScaleChoicesBox from './ScaleChoicesBox';
import { theme } from '@arnold/common';
import { ReactComponent as CommentIcon } from './icons/CommentIcon.svg';
import { useTranslation } from 'react-i18next';

const ScaleStatementContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  width: 100%;

  @media (max-width: ${theme.breakpoint.small}) {
    margin-bottom: 12px;
    width: 100%;
  }
`;

const Statement = styled.div`
  font-size: ${theme.typography.body.medium.regular.fontSize};
  font-weight: ${theme.typography.body.medium.regular.fontWeight};
  color: ${theme.colors.text.primary};
  margin-bottom: 4px;
  margin-left: 3px;
`;

const CommentContainer = styled.div`
  margin-top: 16px;

  @media (max-width: ${theme.breakpoint.small}) {
    margin-top: 12px;
  }
`;

const CommentLabel = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.body.medium.regular.fontSize};
  cursor: pointer;
`;

const CommentInput = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #d2d5d6;
  border-radius: 8px;
  resize: none;
  height: 80px;
  overflow-y: auto;
  transition: border-color 0.2s ease;
  &:focus {
    outline: none;
    border-color: ${theme.colors.actionPrimary.default};
    box-shadow: 0 0 0 1px ${theme.colors.actionPrimary.default};
  }
`;

interface ScaleStatementBoxProps {
  statement: string;
  choices: string[];
  escapeOptionIndex?: number | null;
  onChoiceChange: (index: number) => void;
  onCommentChange: (comment: string | null) => void;
  selectedChoice: number | null;
  comment: string | null;
}

const ScaleStatementBox: React.FC<ScaleStatementBoxProps> = ({
  statement,
  choices,
  escapeOptionIndex,
  onChoiceChange,
  onCommentChange,
  selectedChoice,
  comment,
}) => {
  const { t } = useTranslation('scaleStatements');

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    onCommentChange(value);
  };
  const showCommentInput = comment != null;

  const onCommentBlur = comment == null || comment.length === 0 ? () => onCommentChange(null) : undefined;

  return (
    <ScaleStatementContainer>
      <Statement>{statement}</Statement>
      <ScaleChoicesBox
        choices={choices}
        escapeOptionIndex={escapeOptionIndex}
        onChange={onChoiceChange}
        selectedChoice={selectedChoice ?? undefined}
      />
      <CommentContainer>
        {!showCommentInput ? (
          <CommentLabel onClick={() => onCommentChange(comment ?? '')}>
            <CommentIcon /> <span>{t('writeComment')}</span>
          </CommentLabel>
        ) : (
          <CommentInput
            value={comment}
            onChange={handleCommentChange}
            maxLength={1000}
            autoFocus
            onBlur={onCommentBlur}
          />
        )}
      </CommentContainer>
    </ScaleStatementContainer>
  );
};

export default ScaleStatementBox;
